import axios from "axios";

/* ------------------------------------------------------------------ *
 * Frozen HTTP contract (BGC-6683)
 *
 * GET    /releases                → { items, total, hasMore } of Release
 * POST   /releases                → Release
 * GET    /releases/:id            → Release (bare document)
 * PUT    /releases/:id            → Release
 * DELETE /releases/:id            → { ok: true }
 * GET    /release-groups          → { items, total, hasMore } of ReleaseGroup
 * POST   /release-groups          → ReleaseGroup
 * PUT    /release-groups/:id      → ReleaseGroup
 * DELETE /release-groups/:id      → { ok: true }   (untags; releases survive)
 * PUT    /product-items/:id/releases → ProductItem  body: { releaseIds }
 * PUT    /v1/ideas/:id/releases   → IdeaReleaseAssignment  body: { releaseIds }
 *
 * Both assignment routes are a FULL REPLACEMENT — the body's `releaseIds`
 * becomes the complete set and `[]` clears it. Any set of existing ids is
 * accepted: a group is a tag, so two releases sharing one is ordinary rather
 * than a 400. Do not add merge semantics.
 *
 * Releases consolidate by name. One release carries an `externalRefs` entry per
 * source that means it — the same name in two Jira projects, or in Jira and
 * ProductBoard, is one release with several refs, and the Jira project rides on
 * `projectKey` rather than becoming a group.
 *
 * An idea and a product item each carry their own `releaseIds`, and they are
 * TWO DIFFERENT SETS. An idea's own set is written with
 * `PUT /v1/ideas/:id/releases`; the set on the product item it links to
 * (`idea.linkedProductItemId`) belongs to that item and is only shown on the
 * idea page, never written from it. Never merge one into the other, and never
 * write one through the other — two ideas sharing a product item now keep
 * their own releases.
 *
 * `idea.releaseIds` is OPTIONAL and absent on every idea never assigned one:
 * read it as `idea.releaseIds || []`, never as a guaranteed array.
 * ------------------------------------------------------------------ */

export type ReleaseStatus = "upcoming" | "in_progress" | "completed";

export type ReleaseVisibility = "internal" | "shareable";

export const DEFAULT_RELEASE_VISIBILITY: ReleaseVisibility = "internal";

export interface ReleaseExternalRef {
  provider: "jira" | "productboard";
  cloudId?: string;
  projectKey?: string;
  externalId: string;
  externalName: string;
  linkType: "manual" | "pushed" | "imported";
}

export interface Release {
  _id: string;
  /**
   * The groups this release is tagged with. A group is a label, not a parent: a release may
   * carry several, and an empty array is a real state — a release synced from Jira arrives
   * untagged, because the Jira project is recorded on `externalRefs[].projectKey` rather than
   * turned into a group.
   */
  releaseGroupIds: string[];
  name: string;
  description?: string;
  /**
   * Read-only from here: written by the Phase 5/6 importers through the DAL,
   * never over HTTP, and rejected on create and update.
   */
  externalRefs?: ReleaseExternalRef[];
  status: ReleaseStatus;
  /**
   * Archived at the source — `release.archived` in ProductBoard, `version.archived` in Jira.
   * Read-only from here: the create and update schemas reject it, and nothing in the picker
   * or the filter hides an archived release yet. An archived release is still assigned to its
   * product items, because ProductBoard archives one without clearing the assignments.
   */
  archived?: boolean;
  startDate?: string;
  endDate?: string;
  ownerId?: string;
  visibility: ReleaseVisibility;
}

export interface ReleaseGroup {
  _id: string;
  name: string;
  description?: string;
  /** Where releases land when none is named. No UI yet. */
  isDefault: boolean;
}

/**
 * Every backend 400 and 404 returns this envelope — a Joi validation failure
 * and a business-rule rejection are the same shape. Only success bodies were
 * ever in the frozen contract, so read errors through
 * `getApiErrorMessage` (`api/error-message.ts`) rather than reaching into a response by hand.
 */
export interface ApiErrorBody {
  success: false;
  error: {
    /** The numeric HTTP status, mirroring the repo's `handleRequestError`. */
    code: number;
    message: string;
  };
}

/** The list envelope every collection endpoint returns. */
export interface PaginatedResult<T> {
  items: T[];
  total: number;
  hasMore: boolean;
}

/** `pageSize` caps at 1000 server-side — asking for more truncates silently. */
export interface ListParams {
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortDir?: "asc" | "desc";
  searchQuery?: string;
}

/* ------------------------------------------------------------------ *
 * Write DTOs
 *
 * The create and update schemas are Joi `.unknown(false)`. A server-owned
 * field in the body is a **400, not a silent ignore** — that covers `_id`,
 * `key` and `externalRefs`, and still `index`, now as an unknown key.
 *
 * So do NOT "simplify" an update by spreading a fetched release into it:
 *
 *     updateRelease(auth0, id, { ...release, name });   // guaranteed 400
 *
 * Build the patch from the fields you actually mean to change. The
 * `Partial<CreateRelease>` typing below is what stops this at compile time.
 * ------------------------------------------------------------------ */

/**
 * The server owns `_id`, which is also what orders the list, so it does not
 * appear here. `key`, `archived` and `externalRefs` are server-owned and are
 * rejected outright on create and update.
 */
export interface CreateRelease {
  /** Optional: a release may carry no group tag at all. */
  releaseGroupIds?: string[];
  name: string;
  description?: string;
  status?: ReleaseStatus;
  startDate?: string;
  endDate?: string;
  ownerId?: string;
  visibility?: ReleaseVisibility;
}

/** Inherits the exclusions above: no `_id`, `key` or `externalRefs`. */
export type UpdateRelease = Partial<CreateRelease>;

export interface CreateReleaseGroup {
  name: string;
  description?: string;
  isDefault?: boolean;
}

export type UpdateReleaseGroup = Partial<CreateReleaseGroup>;

/**
 * The join the ideas list already performs. Only `releaseIds` matters here —
 * everything else on a product item is owned by the delivery integrations and
 * is passed through untouched.
 */
export interface ProductItem {
  _id: string;
  releaseIds?: string[];
  [key: string]: any;
}

/**
 * What `PUT /v1/ideas/:id/releases` returns: the idea's assignment shape, not
 * the whole idea. `releaseIds` is the idea's OWN set — the linked product
 * item's releases are a separate set and are not part of this response.
 */
export interface IdeaReleaseAssignment {
  _id: string;
  accountId: string;
  releaseIds: string[];
}

/**
 * The whole body of both assignment routes. One key, and it is the complete
 * desired set — not a delta. `{ releaseIds: [] }` clears.
 */
export interface UpdateProductItemReleases {
  releaseIds: string[];
}

export interface OkResponse {
  ok: true;
}

const RELEASES_PATH = "releases";
const RELEASE_GROUPS_PATH = "release-groups";
const PRODUCT_ITEMS_PATH = "product-items";
const IDEAS_V1_PATH = "v1/ideas";

const apiDomain = () => window.__RUNTIME_CONFIG__.REACT_APP_API_DOMAIN;

/* ----------------------------- Releases ---------------------------- */

/**
 * `signal` lets a caller abandon a request that has been superseded — the search box passes
 * React Query's, so a keystroke that lands mid-flight cancels the request the previous one
 * started instead of leaving it to finish into a cache nobody reads. Mirrors
 * `api/discoveryAgent.ts`.
 */
export const listReleases = async (
  auth0: any,
  params?: ListParams,
  signal?: AbortSignal
): Promise<PaginatedResult<Release>> => {
  const response = await axios.get(`${apiDomain()}/${RELEASES_PATH}`, {
    params,
    ...(signal && { signal }),
  });
  return response.data;
};

export const getRelease = async (auth0: any, id: string): Promise<Release> => {
  const response = await axios.get(
    `${apiDomain()}/${RELEASES_PATH}/${id}`,
  );
  return response.data;
};

export const createRelease = async (
  auth0: any,
  release: CreateRelease
): Promise<Release> => {
  const response = await axios.post(
    `${apiDomain()}/${RELEASES_PATH}`,
    release,
  );
  return response.data;
};

export const updateRelease = async (
  auth0: any,
  id: string,
  update: UpdateRelease
): Promise<Release> => {
  const response = await axios.put(
    `${apiDomain()}/${RELEASES_PATH}/${id}`,
    update,
  );
  return response.data;
};

export const deleteRelease = async (
  auth0: any,
  id: string
): Promise<OkResponse> => {
  const response = await axios.delete(
    `${apiDomain()}/${RELEASES_PATH}/${id}`,
  );
  return response.data;
};

/* -------------------------- Release groups ------------------------- */

export const listReleaseGroups = async (
  auth0: any,
  params?: ListParams
): Promise<PaginatedResult<ReleaseGroup>> => {
  const response = await axios.get(`${apiDomain()}/${RELEASE_GROUPS_PATH}`, {
    params,
  });
  return response.data;
};

export const createReleaseGroup = async (
  auth0: any,
  group: CreateReleaseGroup
): Promise<ReleaseGroup> => {
  const response = await axios.post(
    `${apiDomain()}/${RELEASE_GROUPS_PATH}`,
    group,
  );
  return response.data;
};

export const updateReleaseGroup = async (
  auth0: any,
  id: string,
  update: UpdateReleaseGroup
): Promise<ReleaseGroup> => {
  const response = await axios.put(
    `${apiDomain()}/${RELEASE_GROUPS_PATH}/${id}`,
    update,
  );
  return response.data;
};

export const deleteReleaseGroup = async (
  auth0: any,
  id: string
): Promise<OkResponse> => {
  const response = await axios.delete(
    `${apiDomain()}/${RELEASE_GROUPS_PATH}/${id}`,
  );
  return response.data;
};

/* ------------------ Release assignment (two targets) ---------------- */

/**
 * Replaces a product item's releases wholesale and returns the updated item.
 *
 * `releaseIds` is the complete desired set: pass `[]` to clear, and never a
 * delta. Every set of existing ids is accepted — groups are tags, so two
 * releases sharing one is ordinary.
 */
export const updateProductItemReleases = async (
  auth0: any,
  productItemId: string,
  releaseIds: string[]
): Promise<ProductItem> => {
  const body: UpdateProductItemReleases = { releaseIds };
  const response = await axios.put(
    `${apiDomain()}/${PRODUCT_ITEMS_PATH}/${productItemId}/releases`,
    body,
  );
  return response.data;
};

/**
 * Replaces an idea's OWN releases wholesale and returns its assignment shape
 * (`{_id, accountId, releaseIds}`) — not the idea, and not the linked product
 * item's releases, which this call leaves untouched.
 *
 * `releaseIds` is the complete desired set: pass `[]` to clear, never a delta.
 * An id the account does not own is a 400 carrying
 * `One or more releaseIds do not exist for this account`; an unknown idea is a
 * 404.
 */
export const updateIdeaReleases = async (
  auth0: any,
  ideaId: string,
  releaseIds: string[]
): Promise<IdeaReleaseAssignment> => {
  const body: UpdateProductItemReleases = { releaseIds };
  const response = await axios.put(
    `${apiDomain()}/${IDEAS_V1_PATH}/${ideaId}/releases`,
    body,
  );
  return response.data;
};
